import React, { useState, useRef, useEffect } from 'react';
import './ZoomMenu.css';

interface ZoomMenuProps {
  zoomLevel: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onZoomReset: () => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  highlightOvertime: boolean;
  onToggleHighlightOvertime: () => void;
  overtimeBuffer: number;
  onOvertimeBufferChange: (minutes: number) => void;
}

const OVERTIME_MIN = 0;
const OVERTIME_MAX = 120;

const ZoomMenu: React.FC<ZoomMenuProps> = ({
  zoomLevel,
  onZoomIn,
  onZoomOut,
  onZoomReset,
  onExpandAll,
  onCollapseAll,
  highlightOvertime,
  onToggleHighlightOvertime,
  overtimeBuffer,
  onOvertimeBufferChange,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Spinner handlers
  const handleBufferChange = (delta: number) => {
    let next = overtimeBuffer + delta;
    if (next < OVERTIME_MIN) next = OVERTIME_MIN;
    if (next > OVERTIME_MAX) next = OVERTIME_MAX;
    onOvertimeBufferChange(next);
  };

  return (
    <div className="zoom-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="zoom-menu-button"
        aria-label="Open zoom menu"
        onClick={() => setOpen((v) => !v)}
      >
        ☰
      </button>
      {open && (
        <div
          className="zoom-menu-popup"
          ref={menuRef}
        >
          <div className="zoom-menu-zoom-row">
            <button onClick={onZoomOut} disabled={zoomLevel <= 0.5}>-</button>
            <span className="zoom-menu-zoom-label">{Math.round(zoomLevel * 100)}%</span>
            <button onClick={onZoomReset}>Reset</button>
            <button onClick={onZoomIn} disabled={zoomLevel >= 5}>+</button>
          </div>
          <hr className="zoom-menu-divider" />
          <button className="zoom-menu-action" onClick={onExpandAll}>Expand All Details</button>
          <button className="zoom-menu-action" onClick={onCollapseAll}>Collapse All Details</button>
          <hr className="zoom-menu-divider" />
          <button
            className={`zoom-menu-action${highlightOvertime ? ' active' : ''}`}
            onClick={onToggleHighlightOvertime}
            aria-pressed={highlightOvertime}
            style={{
              fontWeight: highlightOvertime ? 700 : 500,
              background: highlightOvertime
                ? 'linear-gradient(90deg, #fffde7 0%, #ffe082 100%)'
                : undefined,
              color: highlightOvertime ? '#b26a00' : undefined,
              borderColor: highlightOvertime ? '#ffd54f' : undefined,
            }}
          >
            {highlightOvertime ? '✓ ' : ''}Highlight overtime teachers
          </button>
          {highlightOvertime && (
            <div className="zoom-menu-overtime-buffer">
              <label htmlFor="overtime-buffer-spinner" style={{ fontSize: 13, marginRight: 8 }}>
                Buffer before overtime:
              </label>
              <div className="zoom-menu-spinner">
                <button
                  aria-label="Decrease buffer"
                  onClick={() => handleBufferChange(-5)}
                  disabled={overtimeBuffer <= OVERTIME_MIN}
                  type="button"
                >−</button>
                <input
                  id="overtime-buffer-spinner"
                  type="number"
                  min={OVERTIME_MIN}
                  max={OVERTIME_MAX}
                  step={5}
                  value={overtimeBuffer}
                  onChange={e => {
                    let val = parseInt(e.target.value, 10);
                    if (isNaN(val)) val = OVERTIME_MIN;
                    if (val < OVERTIME_MIN) val = OVERTIME_MIN;
                    if (val > OVERTIME_MAX) val = OVERTIME_MAX;
                    // Snap to nearest 5
                    val = Math.round(val / 5) * 5;
                    onOvertimeBufferChange(val);
                  }}
                  style={{
                    width: 40,
                    textAlign: 'center',
                    fontSize: 14,
                    margin: '0 6px',
                    borderRadius: 4,
                    border: '1px solid #bbb',
                  }}
                />
                <button
                  aria-label="Increase buffer"
                  onClick={() => handleBufferChange(5)}
                  disabled={overtimeBuffer >= OVERTIME_MAX}
                  type="button"
                >+</button>
                <span style={{ marginLeft: 6, fontSize: 13 }}>min</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ZoomMenu;
